import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Btn, Field } from "@/components/shared";

export const MfaStep = ({ mfa, onVerify, onCancel }) => {
  const enroll = mfa.mfa_required === "enroll";
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (enroll) api.post("/auth/mfa/setup", { challenge: mfa.challenge }).then(({ data }) => setSetup(data)).catch((e) => setError(errMsg(e)));
  }, [enroll, mfa.challenge]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try { await onVerify(mfa.challenge, code); } catch (err) { setError(errMsg(err)); setCode(""); } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="mfa-form">
      <div className="flex items-center gap-2 text-blue-700"><ShieldCheck className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-wider">2-step verification</span></div>
      <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">{enroll ? "Set up authenticator" : "Enter authenticator code"}</h2>
      {enroll ? (
        <div className="mt-4 space-y-3 text-sm text-slate-600">
          <p>1. Install <b>Google Authenticator</b> or <b>Microsoft Authenticator</b> on your phone.</p>
          <p>2. Tap <b>+</b> and scan this QR code.</p>
          <div className="flex justify-center rounded-lg border bg-white p-4" data-testid="mfa-qr">{setup ? <QRCodeSVG value={setup.otpauth_uri} size={176} /> : <div className="h-[176px] w-[176px] animate-pulse bg-slate-100" />}</div>
          {setup && <p className="text-xs">Can't scan? Enter key: <span className="select-all break-all font-mono font-semibold text-slate-800" data-testid="mfa-secret">{setup.secret}</span></p>}
          <p>3. Enter the 6-digit code shown in the app.</p>
        </div>
      ) : <p className="mt-1 text-sm text-slate-500">Open your authenticator app and enter the 6-digit code for ShubhPay Admin.</p>}
      <div className="mt-6 space-y-4">
        <Field label="6-digit code">
          <input className="np-input text-center font-mono text-xl tracking-[0.5em]" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="mfa-code-input" required />
        </Field>
        {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="mfa-error">{error}</div>}
        <Btn type="submit" loading={loading} disabled={code.length !== 6} className="w-full" data-testid="mfa-submit-button">Verify &amp; login</Btn>
        <button type="button" onClick={onCancel} className="w-full text-xs font-semibold text-slate-500 hover:underline" data-testid="mfa-cancel">Back to login</button>
      </div>
    </form>
  );
};
